import React, { useState } from 'react';
import axios from 'axios';
import SiteHeader from '@/components/SiteHeader';
import SiteFooter from '@/components/SiteFooter';
import CartDrawer from '@/components/CartDrawer';
import SocialLinksRow from '@/components/SocialLinksRow';
import { Mail, Send, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  const [cartOpen, setCartOpen] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        subject: form.subject.trim() || null,
        message: form.message.trim(),
      });
      toast.success('Message sent — I\'ll get back to you shortly.');
      setForm({ name: '', email: '', subject: '', message: '' });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || 'Could not send your message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#09090B] text-white">
      <SiteHeader onCartClick={() => setCartOpen(true)} />

      <main className="flex-1 max-w-3xl mx-auto w-full px-6 lg:px-10 py-16">
        <div className="mb-10 text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#00F0FF]/10 border border-[#00F0FF]/30 mb-5">
            <Mail className="text-[#00F0FF]" size={22} />
          </div>
          <h1 className="text-3xl md:text-4xl font-heading font-bold mb-2" data-testid="contact-title">Get in Touch</h1>
          <p className="text-[#A1A1AA]">Bookings, custom edits, licensing, or just to say hi</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-[#121214] border border-[#27272A] rounded-lg p-8 space-y-5" data-testid="contact-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="block text-sm font-medium mb-2">Name *</label>
              <Input
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="bg-[#18181B] border-[#27272A]"
                placeholder="Your name"
                data-testid="contact-name-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Email *</label>
              <Input
                required
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="bg-[#18181B] border-[#27272A]"
                placeholder="you@example.com"
                data-testid="contact-email-input"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Subject</label>
            <Input
              value={form.subject}
              onChange={(e) => setForm({ ...form, subject: e.target.value })}
              className="bg-[#18181B] border-[#27272A]"
              placeholder="e.g., Booking inquiry"
              data-testid="contact-subject-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Message *</label>
            <textarea
              required
              rows={6}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              className="w-full px-3 py-2 bg-[#18181B] border border-[#27272A] rounded-md text-sm text-white placeholder:text-[#A1A1AA] focus:outline-none focus:border-[#00F0FF]"
              placeholder="Tell me what you need..."
              data-testid="contact-message-input"
            />
          </div>
          <Button
            type="submit"
            disabled={sending}
            className="w-full bg-[#00F0FF] hover:bg-[#33F3FF] text-black font-medium rounded-full h-12"
            data-testid="contact-submit-button"
          >
            {sending ? <Loader2 className="animate-spin" size={18} /> : (<><Send size={16} className="mr-2" /> Send Message</>)}
          </Button>
        </form>

        {/* Prefer a DM? — steer visitors to Messenger + the rest of the socials. */}
        <div className="mt-6 bg-[#121214] border border-[#27272A] rounded-lg p-6 space-y-4" data-testid="contact-socials">
          <div className="text-center">
            <p className="text-xs uppercase tracking-[0.2em] text-[#71717A] mb-2">Or reach me on</p>
            <p className="text-sm text-[#A1A1AA]">Prefer a quick message? Ping me on any of these — I check them daily.</p>
          </div>
          <SocialLinksRow featured="messenger" />
        </div>
      </main>

      <SiteFooter />
      <CartDrawer open={cartOpen} onClose={() => setCartOpen(false)} />
    </div>
  );
};

export default ContactPage;
